import React, { createContext, useContext, useState, useCallback } from 'react';

/* Lightweight demo auth for the pixel-perfect reddy pages ONLY (separate from the
   old app's `reddyannabook_user`). Flips the ReddyLayout header between logged-out
   (Signup/Login) and logged-in (Bal/Exp + account dropdown). No real backend — the
   login modal calls login() for a demo session. */

export const DEMO_USER = {
  id: 'demo123',
  bal: '0',
  exp: '0',
  wallet: '0.00',
  netExposure: '0.00',
  bonus: '0.00',
  availableWithdrawal: '0.00',
  awaitingBonus: '1000',
  availableChips: '0/-',
  exposureChips: '0.00/-',
  totalChips: '0/-',
  profitLoss: '0.00/-',
};

const KEY = 'reddy_demo_user';
const REAL_KEY = 'reddyannabook_user';
const ReddyAuthContext = createContext(null);

/* Map a real API user (id/username/balance…) to the header display shape AccountMenu
   reads (bal/exp/wallet/…). A demo user already has those fields, so pass it through.
   NOTE: live balances aren't in the login response — they fall back to 0 here until the
   balance API is wired into the header. The auth (token) is fully real regardless. */
function toDisplay(u) {
  if (!u || typeof u !== 'object') return null;
  if (u.bal !== undefined) return u; // already display-shaped (demo)
  return {
    ...u,
    id: u.username || u.display_name || u.name || u.id || 'user',
    bal: u.balance ?? '0',
    exp: u.exposure ?? '0',
    wallet: u.balance ?? '0.00',
    netExposure: u.exposure ?? '0.00',
    bonus: u.bonus_balance ?? '0.00',
    availableWithdrawal: u.available_credit ?? '0.00',
    awaitingBonus: u.awaiting_bonus ?? '0',
  };
}

export function ReddyAuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const real = localStorage.getItem(REAL_KEY);   // a real login wins
      if (real) return toDisplay(JSON.parse(real));
      const demo = localStorage.getItem(KEY);         // else a demo session
      return demo ? JSON.parse(demo) : null;
    } catch { return null; }
  });
  const login = useCallback((u = DEMO_USER) => {
    const d = toDisplay(u);
    setUser(d);
    // real users are persisted as reddyannabook_user by the login flow; only the
    // no-backend demo session is persisted here under its own key.
    try { if (!u || u === DEMO_USER || u.id === 'demo123') localStorage.setItem(KEY, JSON.stringify(d)); } catch (_) {}
  }, []);
  const logout = useCallback(() => {
    setUser(null);
    try {
      localStorage.removeItem(KEY);
      localStorage.removeItem(REAL_KEY);
      localStorage.removeItem('reddyannabook_token');
      localStorage.removeItem('must_change_password');
    } catch (_) {}
  }, []);
  return (
    <ReddyAuthContext.Provider value={{ user, login, logout }}>
      {children}
    </ReddyAuthContext.Provider>
  );
}

/* Safe default so components never crash if the provider is absent. */
export function useReddyAuth() {
  return useContext(ReddyAuthContext) || { user: null, login: () => {}, logout: () => {} };
}
